// Supabase cloud sync for AppState.
//
// Wire-up shape:
//   - `syncOnBoot()` runs once at app start: if the device has creds,
//     pull the cloud state, merge it with what's in localStorage, and
//     save the result back. If the cloud was ahead, the next `loadState`
//     picks up the merged version.
//   - `schedulePush()` is called from `saveState()` and debounces a push
//     to the cloud. If the push collides with a newer cloud version, it
//     pulls + re-merges + retries once.
//   - `register()` / `redeemPairCode()` / `createPairCode()` /
//     `disconnect()` are the explicit user-driven actions exposed by
//     the settings view.
//
// All Edge Functions are reachable at
// `${VITE_SUPABASE_URL}/functions/v1/<name>` and require the project
// anon key as the `apikey` header (Supabase's gateway requirement).
// Our actual auth is the `x-device-token` header.
//
// If env vars aren't configured (local dev without a Supabase project
// linked), sync is a no-op and we log once on first attempt.

import { buildInitialState, loadState, saveState, setSaveHook } from "./storage";
import type {
  AppState,
  ArcadeBest,
  ArcadeBests,
  ArcadeMode,
  CountryStats,
  LeaderboardEntry,
  RegionProgress,
} from "./types";

const SYNC_KEY = "geo-sync-v1";
const PUSH_DEBOUNCE_MS = 2000;

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL as string | undefined;
const SUPABASE_ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY as string | undefined;

export interface SyncCreds {
  profileId: string;
  deviceToken: string;
}

interface PersistedSync {
  creds: SyncCreds;
  lastVersion: number;
}

let warnedAboutEnv = false;
const ensureEnv = (): boolean => {
  if (SUPABASE_URL && SUPABASE_ANON_KEY) return true;
  if (!warnedAboutEnv) {
    warnedAboutEnv = true;
    console.info(
      "[sync] VITE_SUPABASE_URL / VITE_SUPABASE_ANON_KEY not set — running local-only.",
    );
  }
  return false;
};

// ---- persisted creds -------------------------------------------------------

export const loadSyncCreds = (): PersistedSync | null => {
  try {
    const raw = localStorage.getItem(SYNC_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<PersistedSync>;
    if (
      parsed?.creds?.profileId &&
      parsed.creds.deviceToken &&
      typeof parsed.lastVersion === "number"
    ) {
      return parsed as PersistedSync;
    }
    return null;
  } catch {
    return null;
  }
};

const saveSyncCreds = (s: PersistedSync): void => {
  localStorage.setItem(SYNC_KEY, JSON.stringify(s));
};

export const clearSyncCreds = (): void => {
  localStorage.removeItem(SYNC_KEY);
};

export const isSynced = (): boolean => loadSyncCreds() !== null;

// ---- HTTP helpers ----------------------------------------------------------

const fnUrl = (name: string): string =>
  `${SUPABASE_URL}/functions/v1/${name}`;

const callFn = async <T>(
  name: string,
  body: unknown,
  deviceToken?: string,
): Promise<{ ok: true; status: number; data: T } | { ok: false; status: number; data: unknown }> => {
  const res = await fetch(fnUrl(name), {
    method: "POST",
    headers: {
      "content-type": "application/json",
      apikey: SUPABASE_ANON_KEY ?? "",
      ...(deviceToken ? { "x-device-token": deviceToken } : {}),
    },
    body: JSON.stringify(body ?? {}),
  });
  const data = (await res.json().catch(() => ({}))) as unknown;
  if (res.ok) return { ok: true, status: res.status, data: data as T };
  return { ok: false, status: res.status, data };
};

// ---- public actions --------------------------------------------------------

export const register = async (): Promise<SyncCreds> => {
  if (!ensureEnv()) throw new Error("sync not configured");
  const result = await callFn<{ profile_id: string; device_token: string; version: number }>(
    "register",
    {},
  );
  if (!result.ok) throw new Error(`register failed: ${result.status}`);
  const creds: SyncCreds = {
    profileId: result.data.profile_id,
    deviceToken: result.data.device_token,
  };
  saveSyncCreds({ creds, lastVersion: result.data.version });
  // Push current local state up immediately so the new profile reflects
  // whatever the user already has on this device.
  await pushOnce(loadState());
  return creds;
};

export const redeemPairCode = async (code: string): Promise<SyncCreds> => {
  if (!ensureEnv()) throw new Error("sync not configured");
  const result = await callFn<{ profile_id: string; device_token: string }>(
    "pair-redeem",
    { code },
  );
  if (!result.ok) throw new Error(`redeem failed: ${result.status}`);
  const creds: SyncCreds = {
    profileId: result.data.profile_id,
    deviceToken: result.data.device_token,
  };
  saveSyncCreds({ creds, lastVersion: 0 });
  // Pull the cloud state and merge with whatever this device has locally.
  await pullAndMerge();
  return creds;
};

export const createPairCode = async (): Promise<{ code: string; expiresAt: string }> => {
  if (!ensureEnv()) throw new Error("sync not configured");
  const persisted = loadSyncCreds();
  if (!persisted) throw new Error("not synced");
  const result = await callFn<{ code: string; expires_at: string }>(
    "pair-create",
    {},
    persisted.creds.deviceToken,
  );
  if (!result.ok) throw new Error(`pair-create failed: ${result.status}`);
  return { code: result.data.code, expiresAt: result.data.expires_at };
};

export const disconnect = (): void => clearSyncCreds();

// ---- leaderboard ----------------------------------------------------------

export type ClaimUsernameResult =
  | { ok: true; username: string }
  | { ok: false; error: "taken" | "invalid_format" | "reserved" | "not_synced" | "network" };

export const claimUsername = async (
  username: string,
): Promise<ClaimUsernameResult> => {
  const persisted = loadSyncCreds();
  if (!persisted || !ensureEnv()) return { ok: false, error: "not_synced" };
  try {
    const result = await callFn<{ ok: true; username: string }>(
      "claim-username",
      { username },
      persisted.creds.deviceToken,
    );
    if (result.ok) return { ok: true, username: result.data.username };
    const errBody = result.data as { error?: string };
    if (errBody.error === "taken") return { ok: false, error: "taken" };
    if (errBody.error === "invalid_format") return { ok: false, error: "invalid_format" };
    if (errBody.error === "reserved") return { ok: false, error: "reserved" };
    return { ok: false, error: "network" };
  } catch {
    return { ok: false, error: "network" };
  }
};

// Push arcade bests up. Called (debounced) after recordArcadeScore writes
// a new best locally. Silently no-ops if the user hasn't claimed a
// username or isn't synced. We don't surface failures — the local state
// is the source of truth and the next push will catch up.
export const pushLeaderboardBests = async (state: AppState): Promise<void> => {
  if (!state.username) return;
  const persisted = loadSyncCreds();
  if (!persisted || !ensureEnv()) return;

  const entries = Object.entries(state.arcadeBests)
    .map(([key, best]) => {
      const sep = key.indexOf(":");
      if (sep < 0) return null;
      const mode = key.slice(0, sep);
      const region = key.slice(sep + 1);
      if (region === "Mixed (cleared regions)") return null;
      return {
        mode,
        region,
        score: best.score,
        achieved_at: new Date(best.achievedAt).toISOString(),
      };
    })
    .filter(<T,>(e: T | null): e is T => e !== null);

  if (entries.length === 0) return;

  try {
    await callFn<{ ok: true; accepted: number }>(
      "leaderboard-push",
      { entries },
      persisted.creds.deviceToken,
    );
  } catch {
    // free-tier failure / offline — local stays authoritative
  }
};

interface LeaderboardPullResponse {
  ok: true;
  entries: Array<{ username: string; score: number; achieved_at: string }>;
}

// Pull a single (mode, region) leaderboard. Anon-callable so we don't
// need creds. Returns null on any error so the caller can fall back to
// the local cache.
export const pullLeaderboard = async (
  mode: ArcadeMode,
  region: string,
  limit = 50,
): Promise<LeaderboardEntry[] | null> => {
  if (!ensureEnv()) return null;
  try {
    const result = await callFn<LeaderboardPullResponse>(
      "leaderboard-pull",
      { mode, region, limit },
    );
    if (!result.ok) return null;
    return result.data.entries.map((e) => ({
      username: e.username,
      score: e.score,
      achievedAt: Date.parse(e.achieved_at),
    }));
  } catch {
    return null;
  }
};

// ---- core sync loop --------------------------------------------------------

// Pull cloud → merge into local → save. Returns the merged state, or
// null if nothing happened. Does NOT push; the caller decides whether
// changes warrant a push.
const pullAndMerge = async (): Promise<AppState | null> => {
  const persisted = loadSyncCreds();
  if (!persisted || !ensureEnv()) return null;

  const result = await callFn<{ state: AppState | Record<string, never>; version: number }>(
    "pull",
    {},
    persisted.creds.deviceToken,
  );
  if (!result.ok) {
    console.warn("[sync] pull failed", result.status, result.data);
    return null;
  }

  const local = loadState();
  const remote = normalizeRemoteState(result.data.state);
  const merged = mergeStates(local, remote);

  saveState(merged); // saveState will schedule its own push
  saveSyncCreds({ creds: persisted.creds, lastVersion: result.data.version });

  return merged;
};

// Push state. If the server says we're stale, pull + merge + push once
// more. We don't retry past that to avoid pathological loops.
const pushOnce = async (state: AppState): Promise<void> => {
  const persisted = loadSyncCreds();
  if (!persisted || !ensureEnv()) return;

  const result = await callFn<
    | { status: "ok"; version: number }
    | { status: "stale"; state: AppState | Record<string, never>; version: number }
  >(
    "push",
    { state, base_version: persisted.lastVersion },
    persisted.creds.deviceToken,
  );

  if (result.ok && result.data.status === "ok") {
    saveSyncCreds({ creds: persisted.creds, lastVersion: result.data.version });
    return;
  }

  if (result.status === 409) {
    const stale = result.data as { state: AppState | Record<string, never>; version: number };
    const merged = mergeStates(state, normalizeRemoteState(stale.state));
    saveState(merged);
    saveSyncCreds({ creds: persisted.creds, lastVersion: stale.version });

    const retry = await callFn<{ status: "ok"; version: number }>(
      "push",
      { state: merged, base_version: stale.version },
      persisted.creds.deviceToken,
    );
    if (retry.ok && retry.data.status === "ok") {
      saveSyncCreds({ creds: persisted.creds, lastVersion: retry.data.version });
    } else {
      console.warn("[sync] retry push failed", retry.status, retry.data);
    }
    return;
  }

  console.warn("[sync] push failed", result.status, result.data);
};

// Boot-time entry point. Registers the saveState→push hook and, if the
// device has creds, pulls from the cloud and merges. Returns after the
// merged state is in localStorage so the rest of the app reads the
// right values from `loadState` on first render.
export const initSync = async (): Promise<void> => {
  setSaveHook((state) => schedulePush(state));
  if (!isSynced() || !ensureEnv()) return;
  try {
    await pullAndMerge();
  } catch (err) {
    console.warn("[sync] initSync threw", err);
  }
};

// Debounced push. Called from saveState() on every state change.
let pushTimer: ReturnType<typeof setTimeout> | null = null;
let pendingState: AppState | null = null;

export const schedulePush = (state: AppState): void => {
  if (!isSynced() || !ensureEnv()) return;
  pendingState = state;
  if (pushTimer) clearTimeout(pushTimer);
  pushTimer = setTimeout(() => {
    pushTimer = null;
    const s = pendingState;
    pendingState = null;
    if (s) void pushOnce(s);
  }, PUSH_DEBOUNCE_MS);
};

// ---- merge logic -----------------------------------------------------------

// If the cloud state was just created (empty object), treat it as an
// initial state so the per-field merge works without special cases.
const normalizeRemoteState = (raw: unknown): AppState => {
  if (!raw || typeof raw !== "object" || !("version" in raw)) {
    return buildInitialState();
  }
  return raw as AppState;
};

const mergeCountryStats = (a: CountryStats, b: CountryStats): CountryStats => {
  // For consecutive counters, take from whichever side saw the country
  // more recently — those counters reflect the latest streak state.
  const newer = b.lastSeen >= a.lastSeen ? b : a;
  return {
    iso: a.iso,
    level: Math.max(a.level, b.level),
    consecutiveCorrect: newer.consecutiveCorrect,
    consecutiveWrong: newer.consecutiveWrong,
    lastSeen: Math.max(a.lastSeen, b.lastSeen),
    // NOTE: max() undercounts when two devices practice the same
    // country offline simultaneously. Accepted tradeoff vs the
    // complexity of per-device delta tracking.
    totalCorrect: Math.max(a.totalCorrect, b.totalCorrect),
    totalWrong: Math.max(a.totalWrong, b.totalWrong),
  };
};

const mergeRegionProgress = (a: RegionProgress, b: RegionProgress): RegionProgress => {
  const cleared = a.cleared || b.cleared;
  const candidates = [a.clearedAt, b.clearedAt].filter(
    (v): v is number => typeof v === "number",
  );
  return {
    cleared,
    clearedAt: candidates.length ? Math.min(...candidates) : null,
  };
};

const mergeArcadeBest = (a: ArcadeBest, b: ArcadeBest): ArcadeBest =>
  b.score > a.score ? b : a;

export const mergeStates = (local: AppState, remote: AppState): AppState => {
  const stats: Record<string, CountryStats> = { ...local.stats };
  for (const [iso, rs] of Object.entries(remote.stats)) {
    const ls = stats[iso];
    stats[iso] = ls ? mergeCountryStats(ls, rs) : rs;
  }

  const regionProgress: Record<string, RegionProgress> = { ...local.regionProgress };
  for (const [region, rp] of Object.entries(remote.regionProgress)) {
    const lp = regionProgress[region];
    regionProgress[region] = lp ? mergeRegionProgress(lp, rp) : rp;
  }

  const arcadeBests: ArcadeBests = { ...local.arcadeBests };
  for (const [key, rb] of Object.entries(remote.arcadeBests)) {
    const lb = arcadeBests[key];
    arcadeBests[key] = lb ? mergeArcadeBest(lb, rb) : rb;
  }

  // Settings: prefer the remote side. The newest device to push wins,
  // which is good enough for two values that change rarely (tolerance
  // and a hint toggle).
  //
  // Username: prefer whichever side has a non-null value. If both sides
  // have one but they differ, keep local — only the local device knows
  // what the user actually claimed most recently.
  //
  // cachedLeaderboards: prefer local (it's a UI cache, not source of
  // truth) and let normal pulls refresh it from the server.
  return {
    version: local.version,
    stats,
    regionProgress,
    arcadeBests,
    settings: { ...local.settings, ...remote.settings },
    username: local.username ?? remote.username ?? null,
    cachedLeaderboards: local.cachedLeaderboards,
  };
};
